import React from 'react';
import {
  Box,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import ConfirmActionPopover from '../ConfirmPopovers/ConfirmActionPopover';
import type Field from '../../model/Field';
import { LangFile } from '../ConfirmPopovers/PopoverUtils';

interface BulkPrescriptionConfirmActionPopoverProps {
  selectedFields: Field[];
  errorFields: Field[];
  fetchingDates: boolean;
  LangFile: {
    BulkPrescriptionPopover: {
      title: string;
      message: string;
      actionText: string;
      cancelText: string;
      disableReason: string;
      status: {
        selectedFields: string;
      };
    };
    CropType: Record<string, string>;
  };
  CropTypeColor: Record<string, string>;
  getFieldName: (field: Field, LangFile: LangFile) => string;
  onConfirm: () => void;
  onCancel: () => void;
  onRemoveField: (field: Field) => void;
}

const BulkPrescriptionConfirmActionPopover: React.FC<BulkPrescriptionConfirmActionPopoverProps> = ({
  selectedFields,
  errorFields,
  fetchingDates,
  LangFile,
  CropTypeColor,
  getFieldName,
  onConfirm,
  onCancel,
  onRemoveField,
}) => {
  const disabled = selectedFields.length < 2 || errorFields.length > 0 || fetchingDates;

  const disabledReason =
    errorFields.length > 0 ? LangFile.BulkPrescriptionPopover.disableReason : '';

  const renderStatus = () => {
    if (fetchingDates) {
      return (
        <Box>
          <Divider style={{ marginTop: 0, marginBottom: '2px', backgroundColor: '#ddd' }} />
          <CircularProgress style={{ display: 'block', margin: '0 auto' }} />
        </Box>
      );
    }

    // No fields selected case
    if (selectedFields.length === 0) {
      return (
        <Box>
          <Divider style={{ marginTop: 0, marginBottom: '2px', backgroundColor: '#ddd' }} />
          <Typography variant="subtitle1" style={{ textAlign: 'center' }}>
            {LangFile.BulkPrescriptionPopover.status.selectedFields} (0 / 0)
          </Typography>

          <Divider style={{ marginTop: 2, marginBottom: 0, backgroundColor: '#ddd' }} />

          <List style={{ width: '100%', maxHeight: '240px', overflow: 'auto' }}>
            <ListItem divider>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    {LangFile.BulkPrescriptionPopover.status.noFieldsSelected.primary}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" style={{ fontStyle: 'italic', color: '#666' }}>
                    {LangFile.BulkPrescriptionPopover.status.noFieldsSelected.secondary}
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Box>
      );
    }

    const errorCount = errorFields.length;

    return (
      <Box>
        <Divider style={{ marginTop: 0, marginBottom: '2px', backgroundColor: '#ddd' }} />

        <Typography variant="subtitle1" style={{ textAlign: 'center' }}>
          {`${LangFile.BulkPrescriptionPopover.status.selectedFields} (${
            selectedFields.length - errorCount
          } / ${selectedFields.length})`}
        </Typography>

        <Divider style={{ marginTop: 2, marginBottom: 0, backgroundColor: '#ddd' }} />

        <List style={{ width: '100%', maxHeight: '240px', overflow: 'auto' }}>
          {selectedFields.map((field) => {
            const hasError = errorFields.some((errorField) => errorField.fieldId === field.fieldId);

            return (
              <ListItem key={field.fieldId} divider>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      border: 2,
                      borderColor: hasError ? 'red' : 'grey.900',
                      bgcolor: CropTypeColor[field.crop],
                    }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      style={{
                        color: hasError ? '#d32f2f' : 'inherit',
                        fontWeight: hasError ? 600 : 400,
                      }}
                    >
                      {getFieldName(field, LangFile)}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      style={{
                        color: hasError ? '#d32f2f' : 'inherit',
                        opacity: hasError ? 0.8 : 1,
                      }}
                    >
                      {LangFile.CropType[field.crop]}
                    </Typography>
                  }
                />

                <ListItemSecondaryAction>
                  <IconButton onClick={() => onRemoveField(field)}>
                    <Clear />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <ConfirmActionPopover
      shown={true}
      LangFile={LangFile}
      action={onConfirm}
      actionTestId="bulk-prescription-continue"
      title={LangFile.BulkPrescriptionPopover.title}
      message={LangFile.BulkPrescriptionPopover.message}
      actionText={LangFile.BulkPrescriptionPopover.actionText}
      cancelText={LangFile.BulkPrescriptionPopover.cancelText}
      cancelAction={onCancel}
      disabled={disabled}
      disabledReason={disabledReason}
      status={renderStatus()}
      style={{
        marginTop: '-18%',
        paddingTop: '2px',
        zIndex: 99,
        position: 'relative',
        pointerEvents: 'auto',
      }}
    />
  );
};

export default BulkPrescriptionConfirmActionPopover;
